/**
 * 「一句话录入」规则解析器（v0.2 T75：由 web 上移至 shared，前后端同一实现）。
 *
 * 只用领域层词表，绝不自造技能：
 * - 学校：调用 detectSchool()；
 * - 技能：先用词表做最长匹配扫描，未命中的片段再交给 normalizeSkill() 兜底；
 *   **两者都没命中的词一律进 unmatched（界面显示为「待归类」），不计入技能、不参与匹配计分**；
 * - 周小时数 / 时段：从文本里认出数字与时段标签。
 *
 * 这里没有任何打分逻辑：只把自然语言拆成「已收录 id + 待归类原词」。
 * 后端 LLM 抽取服务（apps/api extract.service）把它作为无 Key / 超时 / 脏输出的兜底。
 */

import type { SchoolId } from './types.js';
import { SCHOOLS, detectSchool } from './schools.js';
import { SKILLS, normalizeSkill } from './skills.js';
import { SLOT_OPTIONS, detectSlots } from './slots.js';

export { SLOT_OPTIONS, type SlotOption, isSlotOption, detectSlots } from './slots.js';

export interface OneLinerParse {
  raw: string;
  school: SchoolId | null;
  /** 命中的学校别名原词（如「上外」），用于告诉用户"系统根据这个词识别" */
  schoolHint: string | null;
  /** 已收录技能 id（会计分） */
  skills: string[];
  /** 未收录原词（界面显示为「待归类」，不计分） */
  unmatched: string[];
  weeklyHours: number | null;
  slots: string[];
  /** 是否解析出任何可用信息 */
  hasContent: boolean;
}

const LATIN = /[a-z0-9]/;

interface DictEntry {
  id: string;
  term: string;
}

/** 技能词典：id / 中文名 / 别名，长度降序（保证最长匹配优先） */
function buildDict(): DictEntry[] {
  const entries: DictEntry[] = [];
  const push = (id: string, term: string): void => {
    const normalized = term.trim().toLowerCase();
    if (normalized.length >= 2) entries.push({ id, term: normalized });
  };
  for (const skill of SKILLS) {
    push(skill.id, skill.id);
    push(skill.id, skill.name);
    for (const alias of skill.aliases ?? []) push(skill.id, alias);
  }
  return entries.sort((a, b) => b.term.length - a.term.length);
}

const DICT: DictEntry[] = buildDict();

const SEGMENT_SEPARATORS = /[\s,，、。;；:：!！?？/\\|+~—\-_=@#$%^&*()[\]{}<>《》「」【】“”‘’"'`·…和与及]+/;

/** 多字连词（字符类装不下），先替换成空格再切分 */
const CONJUNCTION_PHRASES = ['以及', '还有', '并且', '同时', '外加', '加上'];

const HOURS_PATTERNS: RegExp[] = [
  /(\d{1,2}(?:\.\d)?)\s*(?:个)?\s*(?:小时|h|hr|hrs|hours?)/i,
  /每周[^\d]{0,6}(\d{1,2}(?:\.\d)?)/,
];

/** 多字停用词：只在片段内部做子串移除（如「每周能投入」→「能」） */
const MULTI_STOPWORDS: string[] = [
  '我是', '我会', '我能', '你能', '他会', '她会', '我们', '你们', '他们', '以及', '还有', '并且', '而且', '然后',
  '就是', '可以', '可投入', '能投入', '投入', '每周', '小时', '时间', '在校', '在校生', '学生', '在读', '应届',
  '本科', '专科', '研究生', '专业', '年级', '大学', '学院', '学校', '高校', '读书', '就读', '上海', '松江',
  '经验', '负责', '参与', '参加过', '参加', '团队', '组队', '比赛', '赛事', '项目', '同学', '队友', '希望',
  '想要', '打算', '准备', '平时', '喜欢', '主要', '方向', '相关', '方面', '技能', '能力', '输出', '合作',
  '一起', '使用', '学过', '学习', '目前', '现在', '正在', '大概', '左右', '基本', '比较', '非常', '一点',
  '一些', '个人', '简介', '介绍', '自己', '熟悉', '熟练', '掌握', '擅长', '水平', '优先', '需要', '要求',
  '能够', '没有', '不是', '什么', '怎么', '这个', '那个', '感觉', '觉得', '应该', '可能', '一定', '真的',
  '特别', '十分', '专门', '有空', '有时间', '空闲', '空余', '平时有空', '随时',
].sort((a, b) => b.length - a.length);

/**
 * 长句/分隔符后出现的孤立动词字（「会量子力学」的「会」）。
 * 只在片段紧跟在分隔符之后、或整段文本较长时剥离，避免把「会议记录」这类真词截断。
 */
const LEADING_VERBS = new Set(['会', '懂', '想', '要', '能', '学', '用', '做', '写', '画', '编', '剪', '拍', '玩', '搞']);

function stripLeadingVerbs(text: string, enabled: boolean): string {
  if (!enabled) return text;
  let out = text;
  while (out.length >= 3 && LEADING_VERBS.has(out.slice(0, 1))) out = out.slice(1);
  return out;
}

/** 单字停用词：仅当**整个片段都由这些字组成**时才丢弃，避免误伤「过拟合」这类词 */
const SINGLE_STOPWORDS = new Set([
  '我', '你', '他', '她', '它', '是', '会', '的', '了', '能', '可', '有', '和', '与', '及', '懂', '在', '也',
  '都', '很', '还', '过', '做', '用', '个', '位', '想', '找', '对', '为', '以', '到', '从', '等', '把', '被',
  '给', '只', '又', '再', '就', '才', '而', '或', '好', '多', '少', '学', '干', '看', '说', '写', '些', '么',
  '呢', '吧', '啊', '哦', '嗯', '啦', '呀', '们', '那', '这', '上', '下', '里', '中', '大', '小', '一', '点',
]);

function isLatinWordChar(ch: string | undefined): boolean {
  return typeof ch === 'string' && LATIN.test(ch);
}

/** 纯拉丁/数字术语要求词边界，避免 "sports" 里命中 "ts" */
function boundaryOk(text: string, start: number, end: number, term: string): boolean {
  if (!LATIN.test(term)) return true;
  if (isLatinWordChar(text[start - 1])) return false;
  if (isLatinWordChar(text[end])) return false;
  return true;
}

interface ScanHit {
  id: string;
  start: number;
  end: number;
}

/** 在片段内做「最长优先」的词表扫描，可一次命中多个技能 */
function scanSegment(segment: string): ScanHit[] {
  const text = segment.toLowerCase();
  const hits: ScanHit[] = [];
  let index = 0;
  while (index < text.length) {
    let found: DictEntry | null = null;
    for (const entry of DICT) {
      if (!text.startsWith(entry.term, index)) continue;
      const end = index + entry.term.length;
      if (!boundaryOk(text, index, end, entry.term)) continue;
      found = entry;
      break;
    }
    if (found) {
      hits.push({ id: found.id, start: index, end: index + found.term.length });
      index += found.term.length;
    } else {
      index += 1;
    }
  }
  return hits;
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function removeTerm(text: string, term: string): string {
  if (term.length === 0) return text;
  return text.replace(new RegExp(escapeRegExp(term), 'gi'), ' ');
}

/** 片段清洗：去停用词/标点/数字 → 剩下的才是「待归类」原词 */
function cleanFragment(fragment: string, stripVerb: boolean): string {
  let out = stripLeadingVerbs(fragment, stripVerb);
  for (const word of MULTI_STOPWORDS) out = removeTerm(out, word);
  out = out.replace(/[\s\d\p{P}\p{S}]/gu, '');
  if (out.length === 0) return '';
  let allStop = true;
  for (const ch of out) {
    if (!SINGLE_STOPWORDS.has(ch)) {
      allStop = false;
      break;
    }
  }
  if (allStop) return '';
  return out.length > 24 ? `${out.slice(0, 24)}…` : out;
}

function findAliasInText(text: string, id: SchoolId): string | null {
  const info = SCHOOLS.find((school) => school.id === id);
  if (!info) return null;
  const lower = text.toLowerCase();
  const aliases = [...info.aliases].sort((a, b) => b.length - a.length);
  for (const alias of aliases) {
    if (lower.includes(alias.toLowerCase())) return alias;
  }
  return null;
}

function extractHours(text: string): number | null {
  for (const pattern of HOURS_PATTERNS) {
    const matched = pattern.exec(text);
    const raw = matched?.[1];
    if (typeof raw !== 'string') continue;
    const value = Number(raw);
    if (Number.isFinite(value)) return value;
  }
  return null;
}

function pushUnique(list: string[], value: string): void {
  if (!list.includes(value)) list.push(value);
}

export function parseOneLiner(raw: string): OneLinerParse {
  const text = typeof raw === 'string' ? raw : '';
  const school = detectSchool(text);
  const schoolHint = school ? findAliasInText(text, school) : null;
  const slots = detectSlots(text);
  const weeklyHours = extractHours(text);

  let work = text;
  // 学校：优先整名移除（否则「上海立信会计金融学院」会被拆出技能词「会计」），否则移除命中的别名
  const schoolFullName = school !== null ? SCHOOLS.find((item) => item.id === school)?.name ?? null : null;
  if (schoolFullName !== null && work.toLowerCase().includes(schoolFullName.toLowerCase())) {
    work = removeTerm(work, schoolFullName);
  } else if (schoolHint !== null) {
    work = removeTerm(work, schoolHint);
  }
  for (const phrase of CONJUNCTION_PHRASES) work = removeTerm(work, phrase);
  for (const slot of slots) work = removeTerm(work, slot);
  work = work.replace(/大[一二三四]/g, ' ');
  work = work.replace(/研[一二三]/g, ' ');
  work = work.replace(/专业/g, ' ');
  if (weeklyHours !== null) {
    for (const pattern of HOURS_PATTERNS) {
      work = work.replace(new RegExp(pattern.source, 'g'), ' ');
    }
  }

  const rawSegments = work.split(SEGMENT_SEPARATORS);
  // 长句（≥8 字）里的孤立动词字通常是填充词；短片段可能是真词，保持原样
  const longText = text.trim().length >= 8;

  const matched: string[] = [];
  const leftovers: { text: string; afterSeparator: boolean }[] = [];

  rawSegments.forEach((rawSegment, index) => {
    const segment = rawSegment.trim();
    if (segment.length === 0) return;
    const afterSeparator = index > 0;
    const hits = scanSegment(segment);
    if (hits.length === 0) {
      const fallback = normalizeSkill(segment);
      if (fallback) {
        pushUnique(matched, fallback);
        return;
      }
      leftovers.push({ text: segment, afterSeparator });
      return;
    }
    let cursor = 0;
    for (const hit of hits) {
      pushUnique(matched, hit.id);
      const gap = segment.slice(cursor, hit.start);
      if (gap.trim().length > 0) {
        // 只有片段最前面的空隙才可能"紧跟分隔符"
        leftovers.push({ text: gap, afterSeparator: cursor === 0 ? afterSeparator : false });
      }
      cursor = hit.end;
    }
    const tail = segment.slice(cursor);
    if (tail.trim().length > 0) leftovers.push({ text: tail, afterSeparator: false });
  });

  const unmatched: string[] = [];
  for (const item of leftovers) {
    const cleaned = cleanFragment(item.text, item.afterSeparator || longText);
    if (cleaned.length >= 2 && !matched.includes(cleaned) && !unmatched.includes(cleaned)) {
      unmatched.push(cleaned);
    }
  }

  return {
    raw: text,
    school,
    schoolHint,
    skills: matched,
    unmatched,
    weeklyHours,
    slots,
    hasContent:
      school !== null || matched.length > 0 || unmatched.length > 0 || weeklyHours !== null || slots.length > 0,
  };
}

/** 供界面展示的示例文案（占位提示，不是数据） */
export const ONE_LINER_EXAMPLE = '我是上外的，会 PS 和 Python，每周能投入 15 小时，周末全天有空';
